import React, { ReactNode } from "react";

import { createChangeEvent } from "../utils/form";

export interface SwitchProps {
  label?: string | ReactNode;
  labelPosition?: "left" | "right";
  subLabel?: string | ReactNode;
  disabled?: boolean;
  name?: string;
  icons?: ReactNode[];
  value?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: boolean) => void;
  customStyle?: string;
}

const baseStyle = `
  cursor-pointer inline-block
  after:absolute  after:top-0 after:block  after:content-[''] after:duration-100 checked:after:duration-300
  before:absolute before:top-0 before:inline-block before:content-[''] before:rounded-full after:rounded-full 
`;

const switchStyle = `
  w-8 before:w-8 h-0 before:h-[18px] checked:after:translate-x-3.5 after:w-3.5 after:h-3.5 after:mt-0.5 after:ml-0.5
  before:bg-bg-switch-base hover:before:bg-bg-switch-hover border-0 outline-0
  before:checked:bg-bg-interactive-base hover:before:checked:bg-bg-interactive-hover
  after:bg-bg-switch-handle-default
  disabled:before:bg-bg-disabled disabled:before:hover:bg-bg-disabled disabled:before:border disabled:before:border-border-disabled
  disabled:after:bg-bg-switch-handle-disabled disabled:cursor-not-allowed disabled:before:outline-0
  active:before:outline-focus active:before:outline-offset-1
`;

const Switch = ({
  label,
  labelPosition = "right",
  subLabel,
  disabled = false,
  icons,
  name,
  value,
  onChange,
  customStyle,
}: SwitchProps) => {
  const labelNode = (
    <div className="typography-medium-base">
      <div className="flex items-center space-x-1.5 mb-1 justify-center">
        <span className={disabled ? "text-text-disabled" : "text-text-base"}>{label}</span>
        {icons?.length > 0 && icons.map((icon, index) => (
          <span key={index} className={disabled ? "text-icon-disabled" : "text-icon-bg-base"}>{icon}</span>
        ))}
      </div>
      <span className={disabled ? "text-text-disabled" : "text-text-subtle"}>{subLabel}</span>
    </div>
  );
  return (
    <div className="flex space-x-3">
      {labelPosition === "left" && labelNode}
      <label htmlFor="custom-switch" className="relative inline-block space-x-3 h-6">
        <input
          disabled={disabled}
          id="custom-switch"
          type="checkbox"
          className={`${baseStyle} ${customStyle ?? switchStyle}`}
          checked={value}
          onChange={(e) => {
            onChange(createChangeEvent(name, e.target.checked), e.target.checked);
          }}
        />
      </label>
      {labelPosition === "right" && labelNode}
    </div>
  );
};

export default Switch;
